import { useParams } from "react-router";
import { useCurrentUserID } from "@/api/hooks";
import { useGetRecordsTimeRangeQuery } from "@/api/api";
import { fitIntervalsToSeconds } from "./fitRange";
import { QuickIntervalTarget } from "./quickIntervals";
import { safeName } from "./recordTree";
import getGlobalStacks from "@/state/globalStacks";

/**
 * The interval pair covering every record of one collection, or of the whole
 * space when `collectionID` is undefined. `target` is null while the range is
 * unknown — still loading, or nothing timed in scope.
 *
 * The server widens the scope to the collection *and its descendants*
 * (`get_effective_collection_ids`), which is what makes this meaningful for a
 * collapsed row in the sidebar as well as for the collection page.
 */
export function useCollectionTimeRange(
  collectionID: string | undefined,
  options: { skip?: boolean } = {},
) {
  const userID = useCurrentUserID();
  const { data: timeRange, isFetching } = useGetRecordsTimeRangeQuery(
    { userID, collectionID },
    { skip: options.skip },
  );

  const target =
    timeRange?.min_second != null && timeRange?.max_second != null
      ? fitIntervalsToSeconds(
          BigInt(timeRange.min_second),
          BigInt(timeRange.max_second),
        )
      : null;

  return { target, isFetching, hasRange: !!timeRange?.min_second };
}

/**
 * The interval pair covering every record of the collection currently in the
 * URL (or of the whole space when there is none).
 */
export function useAllRecordsTarget() {
  const { collectionID } = useParams();
  const { target, isFetching } = useCollectionTimeRange(collectionID);

  return { target, isFetching };
}

/**
 * Widens the global time range to fit every record of the current collection —
 * the same jump the "All records" quick interval performs. Null while the range
 * is unknown, so callers can leave the click as a plain no-op.
 */
export function useFitToAllRecords() {
  const { target } = useAllRecordsTarget();

  if (!target) return null;

  return () => {
    getGlobalStacks().setIntervals(target.interval, target.endInterval);
  };
}

/**
 * "1789 - 1799", or a single name when the whole range fits one tick.
 *
 * The granularity is whatever `fitIntervalsToSeconds` settled on, so it adapts
 * on its own: years for a decade, centuries for an era. `safeName` rather than
 * `getName` because the interval type is not known statically and `getName`
 * throws for one it has no format for.
 */
export function formatFittedRange(target: QuickIntervalTarget): string {
  const start = safeName(target.interval);
  if (!target.endInterval) return start;
  return `${start} - ${safeName(target.endInterval)}`;
}
